import React, { useState, useRef, useEffect } from 'react';

const PhotoBooth = () => {
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('tokyo');
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [stream, setStream] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [cameraRetryCount, setCameraRetryCount] = useState(0);
  const [isInitializingCamera, setIsInitializingCamera] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const apiKey = 'AIzaSyAVFf92U-jLB8OZ6wv7n3_WfjddmQHxGNY';

  const locations = [
    {
      id: 'tokyo',
      name: 'Tokyo, Japan',
      prompt: 'in a beautiful Tokyo street with cherry blossoms, neon signs, and traditional architecture, cinematic lighting, ultra realistic'
    },
    {
      id: 'la',
      name: 'Los Angeles, USA',
      prompt: 'in downtown Los Angeles with palm trees, Hollywood hills in background, golden hour lighting, cinematic'
    },
    {
      id: 'paris',
      name: 'Paris, France',
      prompt: 'in front of the Eiffel Tower in Paris, romantic evening lighting, beautiful French architecture'
    },
    {
      id: 'nyc',
      name: 'New York City, USA',
      prompt: 'in Times Square New York City with bright billboards, busy street, urban atmosphere'
    },
    {
      id: 'london',
      name: 'London, UK',
      prompt: 'in London with Big Ben and red telephone boxes, cloudy British weather, classic architecture'
    },
    {
      id: 'bali',
      name: 'Bali, Indonesia',
      prompt: 'in Bali with tropical rice terraces, lush greenery, peaceful temple in background'
    }
  ];

  useEffect(() => {
    return () => {
      // Cleanup camera stream when component unmounts
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Progressive fallback camera constraints
  const getCameraConstraints = (attemptNumber = 0) => {
    const constraints = [
      // Attempt 1: Ideal settings
      {
        video: {
          width: { ideal: 640, max: 1280 },
          height: { ideal: 480, max: 720 },
          facingMode: 'user',
          frameRate: { ideal: 30, min: 15 }
        }
      },
      // Attempt 2: Reduced quality
      {
        video: {
          width: { ideal: 480, max: 640 },
          height: { ideal: 360, max: 480 },
          facingMode: 'user'
        }
      },
      // Attempt 3: Basic settings
      {
        video: {
          width: 320,
          height: 240,
          facingMode: 'user'
        }
      },
      // Attempt 4: Minimal constraints
      {
        video: true
      }
    ];
    
    return constraints[Math.min(attemptNumber, constraints.length - 1)];
  };

  const initializeVideoElement = (video, stream) => {
    return new Promise((resolve, reject) => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          reject(new Error('Video initialization timeout'));
        }
      }, 5000);

      const handleVideoReady = () => {
        if (resolved) return;
        
        console.log('📺 Video event triggered - dimensions:', video.videoWidth, 'x', video.videoHeight);
        console.log('📺 Video ready state:', video.readyState);
        
        if (video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2) {
          resolved = true;
          clearTimeout(timeout);
          
          // Clean up event listeners
          video.onloadedmetadata = null;
          video.onloadeddata = null;
          video.oncanplay = null;
          video.onplaying = null;
          
          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            readyState: video.readyState
          });
        }
      };

      // Set up event listeners
      video.onloadedmetadata = handleVideoReady;
      video.onloadeddata = handleVideoReady;
      video.oncanplay = handleVideoReady;
      video.onplaying = handleVideoReady;
      
      video.onerror = (e) => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          reject(new Error(`Video error: ${e.message || 'Unknown video error'}`));
        }
      };

      // Set the stream and try to play
      video.srcObject = stream;
      
      video.play().catch(playError => {
        console.warn('⚠️ Video play() failed:', playError);
        // Don't reject here, video might still work without autoplay
      });
      
      // Immediate check in case video is already ready
      setTimeout(() => {
        if (!resolved && video.videoWidth > 0 && video.videoHeight > 0) {
          handleVideoReady();
        }
      }, 100);
    });
  };

  const attemptCameraStart = async (attemptNumber = 0) => {
    const maxAttempts = 4;
    
    try {
      console.log(`🎥 Camera attempt ${attemptNumber + 1}/${maxAttempts}`);
      
      const constraints = getCameraConstraints(attemptNumber);
      console.log('📹 Using constraints:', constraints);
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('📹 Camera stream obtained:', mediaStream);
      
      const videoTracks = mediaStream.getVideoTracks();
      if (videoTracks.length === 0) {
        throw new Error('No video tracks in stream');
      }
      
      console.log('📹 Video track settings:', videoTracks[0].getSettings());
      
      if (!videoRef.current) {
        throw new Error('Video element not available');
      }
      
      // Initialize video element with timeout
      const videoInfo = await initializeVideoElement(videoRef.current, mediaStream);
      console.log('✅ Video initialized successfully:', videoInfo);
      
      setStream(mediaStream);
      setIsVideoReady(true);
      // isCameraOn is already set to true before this
      setError(null);
      setCameraRetryCount(0);
      
      return true;
      
    } catch (err) {
      console.error(`❌ Camera attempt ${attemptNumber + 1} failed:`, err);
      
      // Clean up any partial stream
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      if (attemptNumber < maxAttempts - 1) {
        console.log(`🔄 Retrying with fallback constraints...`);
        await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay
        return attemptCameraStart(attemptNumber + 1);
      } else {
        throw err;
      }
    }
  };

  const startCamera = async () => {
    if (isInitializingCamera) {
      console.log('⚠️ Camera initialization already in progress');
      return;
    }
    
    setIsInitializingCamera(true);
    setIsVideoReady(false);
    setError(null);
    
    // Set isCameraOn to true first so the video element renders
    setIsCameraOn(true);
    
    // Wait for React to render the video element
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      await attemptCameraStart();
    } catch (err) {
      console.error('❌ All camera attempts failed:', err);
      
      let errorMessage = 'Unable to access camera. ';
      
      if (err.name === 'NotAllowedError') {
        errorMessage += 'Please allow camera permissions and try again.';
      } else if (err.name === 'NotFoundError') {
        errorMessage += 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage += 'Camera is already in use by another application.';
      } else if (err.message.includes('timeout')) {
        errorMessage += 'Camera initialization timed out. Please try again.';
      } else {
        errorMessage += `${err.message || 'Please check your camera and permissions.'}`;
      }
      
      setError(errorMessage);
      setIsCameraOn(false);
      setIsVideoReady(false);
      setCameraRetryCount(prev => prev + 1);
    } finally {
      setIsInitializingCamera(false);
    }
  };

  const stopCamera = () => {
    console.log('📹 Stopping camera...');
    
    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop();
        console.log('📹 Stopped track:', track.kind, track.label);
      });
      setStream(null);
    }
    
    if (videoRef.current) {
      // Clean up video element
      videoRef.current.srcObject = null;
      videoRef.current.onloadedmetadata = null;
      videoRef.current.onloadeddata = null;
      videoRef.current.oncanplay = null;
      videoRef.current.onplaying = null;
      videoRef.current.onerror = null;
    }
    
    setIsCameraOn(false);
    setIsVideoReady(false);
    setIsInitializingCamera(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      setError('Camera not available. Please start camera first.');
      return;
    }

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');
    
    console.log('📸 Attempting to capture photo...');
    console.log('📸 Video dimensions:', video.videoWidth, 'x', video.videoHeight);
    console.log('📸 Video ready state:', video.readyState);
    console.log('📸 Video paused:', video.paused);
    console.log('📸 Video ended:', video.ended);
    console.log('📸 isVideoReady state:', isVideoReady);
    
    // Check if video is ready and playing
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setError('Video not ready. Please wait for camera to load and try again.');
      return;
    }
    
    if (video.readyState < 2) {
      setError('Camera is still loading. Please wait a moment and try again.');
      return;
    }
    
    if (!isVideoReady) {
      setError('Video is still initializing. Please wait a moment and try again.');
      return;
    }
    
    console.log('📹 Video ready - dimensions:', video.videoWidth, 'x', video.videoHeight);
    console.log('📹 Video ready state:', video.readyState);
    
    try {
      // Optimize image size for API efficiency
      const maxWidth = 800;
      const maxHeight = 600;
      const scale = Math.min(maxWidth / video.videoWidth, maxHeight / video.videoHeight, 1);
      
      canvas.width = video.videoWidth * scale;
      canvas.height = video.videoHeight * scale;
      
      console.log('🎨 Canvas dimensions:', canvas.width, 'x', canvas.height);
      
      // Clear canvas and draw video frame
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageData = canvas.toDataURL('image/jpeg', 0.8); // Slightly higher quality
      const base64Data = imageData.split(',')[1];
      
      console.log('📸 Captured image data length:', base64Data.length);
      console.log('📸 Image data preview:', base64Data.substring(0, 50) + '...');
      console.log('📸 Full image data URL length:', imageData.length);
      
      if (!base64Data || base64Data.length < 100) {
        setError('Failed to capture image data. Please try again.');
        return;
      }
      
      setCapturedPhoto({
        data: base64Data,
        preview: imageData,
        mimeType: 'image/jpeg',
        timestamp: new Date().toISOString()
      });
      
      console.log('✅ Photo captured successfully!');
      
      // Stop camera after capture
      stopCamera();
      setError(null);
    } catch (err) {
      console.error('❌ Error capturing photo:', err);
      setError('Failed to capture photo. Please try again.');
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    setGeneratedImage(null);
    startCamera();
  };

  const clearAll = () => {
    setCapturedPhoto(null);
    setGeneratedImage(null);
    stopCamera();
  };

  const generatePhotoBoothImage = async () => {
    if (!capturedPhoto || isLoading) return;

    // Validate image data
    if (!capturedPhoto.data || capturedPhoto.data.length < 100) {
      setError('Invalid image data. Please retake the photo.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setGeneratedImage(null);

    try {
      const selectedLocationData = locations.find(loc => loc.id === selectedLocation);
      const prompt = `Transform this person ${selectedLocationData.prompt}, keep the person's appearance exactly the same, photorealistic, high quality`;
      
      console.log('📸 Generating photo booth image for location:', selectedLocationData.name);
      console.log('🎨 Using prompt:', prompt);
      
      const parts = [
        { text: prompt },
        {
          inlineData: {
            mimeType: capturedPhoto.mimeType,
            data: capturedPhoto.data
          }
        }
      ];
      
      const requestBody = {
        contents: [
          {
            parts: parts
          }
        ]
      };

      console.log('📤 Request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(
        'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-image:generateContent',
        {
          method: 'POST',
          headers: {
            'x-goog-api-key': apiKey,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Image generation response:', data);

      // Extract the base64 image data
      if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts) {
        const imagePart = data.candidates[0].content.parts.find(part => part.inlineData);
        if (imagePart && imagePart.inlineData) {
          const imageData = `data:${imagePart.inlineData.mimeType};base64,${imagePart.inlineData.data}`;
          setGeneratedImage(imageData);
          console.log('🖼️ Image generated successfully');
        } else {
          throw new Error('No image data found in response');
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('❌ Error generating image:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadImage = () => {
    if (!generatedImage) return;

    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `photo-booth-${selectedLocation}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex flex-col h-full bg-[#ece9d8]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0054e3] to-[#0099ff] text-white px-4 py-2 flex items-center border-b-2 border-[#003c74]">
        <div className="w-6 h-6 mr-2 bg-white rounded flex items-center justify-center">
          <span className="text-blue-600 text-sm font-bold">📸</span>
        </div>
        <div className="flex-1">
          <div className="font-bold text-sm">AI Photo Booth</div>
          <div className="text-xs opacity-90">Take a photo and travel anywhere!</div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 overflow-y-auto">
        {/* Camera/Photo Section */}
        <div className="bg-[#d4d0c8] border-2 border-[#ffffff] p-4 mb-4"
             style={{
               boxShadow: 'inset 1px 1px 0 0 #000000, inset -1px -1px 0 0 #ffffff'
             }}>
          
          {/* Location Selection */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-800 mb-2">
              Choose Your Destination:
            </label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="w-full px-3 py-2 border-2 border-[#7f9db9] focus:outline-none focus:border-[#0054e3] bg-white"
              style={{
                boxShadow: 'inset 1px 1px 0 0 #000000, inset -1px -1px 0 0 #ffffff'
              }}
            >
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          {/* Camera/Photo Area */}
          <div className="border-2 border-[#808080] p-4 bg-black mb-4 text-center"
               style={{
                 boxShadow: 'inset 1px 1px 0 0 #000000, inset -1px -1px 0 0 #ffffff',
                 minHeight: '300px'
               }}>
            {!capturedPhoto && !isCameraOn && (
              <div className="flex flex-col items-center justify-center h-64 text-white">
                <div className="text-6xl mb-4">📸</div>
                <p className="text-lg mb-4">Ready to take your photo?</p>
                <button
                  onClick={startCamera}
                  disabled={isInitializingCamera}
                  className={`text-black border-2 border-[#ffffff] px-6 py-3 font-bold text-sm transition-colors ${
                    isInitializingCamera 
                      ? 'bg-gray-300 cursor-not-allowed' 
                      : 'bg-[#d4d0c8] hover:bg-[#e8e5dd] active:border-[#808080]'
                  }`}
                  style={{
                    boxShadow: '2px 2px 0 0 #000000, -1px -1px 0 0 #808080, 1px 1px 0 0 #ffffff',
                    borderTopColor: '#ffffff',
                    borderLeftColor: '#ffffff',
                    borderRightColor: '#808080',
                    borderBottomColor: '#808080'
                  }}
                >
                  {isInitializingCamera ? '⏳ Initializing...' : '📹 Start Camera'}
                  {cameraRetryCount > 0 && ` (Retry ${cameraRetryCount})`}
                </button>
              </div>
            )}
            
            {isCameraOn && (
              <div className="relative">
                <div className="relative bg-black rounded overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-auto max-h-64 rounded"
                    style={{ 
                      minHeight: '200px',
                      objectFit: 'cover'
                    }}
                    onLoadedData={() => {
                      console.log('📺 Video loaded and ready to play');
                    }}
                    onError={(e) => {
                      console.error('❌ Video error:', e);
                      setError('Video playback error. Please try restarting camera.');
                    }}
                  />
                  {/* Camera status indicator */}
                  <div className={`absolute top-2 right-2 text-white px-2 py-1 rounded text-xs font-bold flex items-center ${
                    isVideoReady ? 'bg-red-600' : 'bg-yellow-600'
                  }`}>
                    <div className={`w-2 h-2 rounded-full mr-1 ${
                      isVideoReady ? 'bg-red-300 animate-pulse' : 'bg-yellow-300 animate-bounce'
                    }`}></div>
                    {isVideoReady ? 'LIVE' : 'LOADING'}
                  </div>
                </div>
                <div className="mt-4 space-x-2">
                  <button
                    onClick={capturePhoto}
                    disabled={!isVideoReady}
                    className={`border-2 border-[#ffffff] px-6 py-2 font-bold text-sm transition-colors rounded-full shadow-lg ${
                      isVideoReady 
                        ? 'bg-red-600 text-white hover:bg-red-700 cursor-pointer' 
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    }`}
                  >
                    📷 {isVideoReady ? 'Capture Photo' : 'Preparing Camera...'}
                  </button>
                  <button
                    onClick={stopCamera}
                    className="bg-gray-600 text-white border-2 border-[#ffffff] px-4 py-2 font-bold text-sm hover:bg-gray-700 transition-colors"
                  >
                    ❌ Stop Camera
                  </button>
                </div>
              </div>
            )}
            
            {capturedPhoto && (
              <div className="relative">
                <div className="relative bg-gray-100 rounded overflow-hidden">
                  <img
                    src={capturedPhoto.preview}
                    alt="Captured photo"
                    className="w-full h-auto max-h-64 rounded"
                    style={{ 
                      minHeight: '200px',
                      objectFit: 'cover'
                    }}
                    onLoad={() => {
                      console.log('✅ Captured photo preview loaded successfully');
                    }}
                    onError={(e) => {
                      console.error('❌ Error loading captured photo preview:', e);
                      setError('Failed to display captured photo. Please try again.');
                    }}
                  />
                  {/* Photo info overlay */}
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs">
                    📸 Captured: {capturedPhoto.timestamp ? new Date(capturedPhoto.timestamp).toLocaleTimeString() : 'Now'}
                  </div>
                </div>
                <div className="mt-4 space-x-2 flex flex-wrap gap-2">
                  <button
                    onClick={generatePhotoBoothImage}
                    disabled={isLoading}
                    className="bg-green-600 text-white border-2 border-[#ffffff] px-6 py-2 font-bold text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
                  >
                    {isLoading ? '⏳ Generating...' : `✨ Travel to ${locations.find(l => l.id === selectedLocation)?.name}`}
                  </button>
                  <button
                    onClick={retakePhoto}
                    className="bg-blue-600 text-white border-2 border-[#ffffff] px-4 py-2 font-bold text-sm hover:bg-blue-700 transition-colors"
                  >
                    🔄 Retake Photo
                  </button>
                  <button
                    onClick={clearAll}
                    className="bg-gray-600 text-white border-2 border-[#ffffff] px-4 py-2 font-bold text-sm hover:bg-gray-700 transition-colors"
                  >
                    🗑️ Clear All
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>


        {/* Loading State */}
        {isLoading && (
          <div className="bg-white border-2 border-[#808080] p-6 mb-4 text-center"
               style={{
                 boxShadow: 'inset 1px 1px 0 0 #000000, inset -1px -1px 0 0 #ffffff'
               }}>
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div className="w-4 h-4 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-4 h-4 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-4 h-4 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
            <p className="text-sm text-gray-700">Generating your image...</p>
            <p className="text-xs text-gray-500 mt-1">This may take a few moments</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-[#ffcccc] border-2 border-[#ff0000] p-4 mb-4"
               style={{
                 boxShadow: 'inset 1px 1px 0 0 #000000, inset -1px -1px 0 0 #ffffff'
               }}>
            <div className="flex items-center space-x-2">
              <span className="text-red-600 font-bold">❌</span>
              <div>
                <p className="font-bold text-red-800">Error generating image:</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Generated Image Preview */}
        {generatedImage && (
          <div className="bg-white border-2 border-[#808080] p-4"
               style={{
                 boxShadow: 'inset 1px 1px 0 0 #000000, inset -1px -1px 0 0 #ffffff'
               }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800">Generated Image:</h3>
              <button
                onClick={handleDownloadImage}
                className="bg-[#d4d0c8] border-2 border-[#ffffff] px-4 py-1 font-bold text-sm hover:bg-[#e8e5dd] active:border-[#808080] transition-colors"
                style={{
                  boxShadow: '2px 2px 0 0 #000000, -1px -1px 0 0 #808080, 1px 1px 0 0 #ffffff',
                  borderTopColor: '#ffffff',
                  borderLeftColor: '#ffffff',
                  borderRightColor: '#808080',
                  borderBottomColor: '#808080'
                }}
              >
                💾 Download
              </button>
            </div>
            
            <div className="border-2 border-[#808080] p-2 bg-[#f0f0f0]"
                 style={{
                   boxShadow: 'inset 1px 1px 0 0 #000000, inset -1px -1px 0 0 #ffffff'
                 }}>
              <img 
                src={generatedImage} 
                alt="Generated image"
                className="w-full h-auto max-w-full rounded"
                style={{ imageRendering: 'auto' }}
              />
            </div>
            
            <div className="mt-2 text-xs text-gray-600">
              <p><strong>Location:</strong> {locations.find(l => l.id === selectedLocation)?.name}</p>
              <p><strong>Generated:</strong> {new Date().toLocaleString()}</p>
            </div>
          </div>
        )}

        {/* Tips Section */}
        {!generatedImage && !isLoading && !capturedPhoto && (
          <div className="bg-[#ffffcc] border-2 border-[#cccc00] p-4 mt-4"
               style={{
                 boxShadow: 'inset 1px 1px 0 0 #000000, inset -1px -1px 0 0 #ffffff'
               }}>
            <h4 className="font-bold text-gray-800 mb-2">📸 How to use Photo Booth:</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Choose your dream destination from the dropdown</li>
              <li>• Click "Start Camera" to activate your webcam</li>
              <li>• Position yourself in the camera frame</li>
              <li>• Click "Capture Photo" to take your picture</li>
              <li>• Click "Travel to [Location]" to generate your photo in that location</li>
              <li>• Download and share your amazing travel photo!</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoBooth;
